import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  Typography,
  FormControlLabel,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import { type ImportedLocation } from 'api/location-importer.api';
import { type GradeRecord } from 'views/DispatchImporters/GradeImporterView';
import {
  GRADE_CATEGORY_COLORS,
  GRADE_ZONE_COLOR,
} from './roadsImporter.constants';
import { gradeCategoryColorHex } from './roadsImporter.helpers';

interface MapGradeBlocksDialogProps {
  location: ImportedLocation | null;
  grades: GradeRecord[];
  onClose: () => void;
  onConfirm: (gradeIds: string[]) => Promise<void>;
}

export function MapGradeBlocksDialog({
  location,
  grades,
  onClose,
  onConfirm,
}: MapGradeBlocksDialogProps): JSX.Element {
  const { t } = useTranslation();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [mapping, setMapping] = useState(false);

  useEffect(() => {
    setSelectedIds(new Set(grades.map((g) => g.id)));
  }, [grades]);

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleConfirm = async () => {
    setMapping(true);
    try {
      await onConfirm(Array.from(selectedIds));
    } finally {
      setMapping(false);
    }
  };

  const fallbackColor = GRADE_CATEGORY_COLORS.other ?? GRADE_ZONE_COLOR;

  return (
    <Dialog
      open={location !== null}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: (t) => alpha(t.palette.primary.main, 0.15),
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 3,
          py: 2,
        }}
      >
        <Typography variant="h6" fontWeight={700}>
          {t('roads_importer_map_grade_blocks_title', {
            name: location?.name ?? '',
          })}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 0, pb: 2 }}>
        {grades.length === 0 ? (
          <Box
            sx={{
              textAlign: 'center',
              py: 4,
            }}
          >
            <LayersOutlinedIcon
              sx={{ fontSize: 32, color: 'text.secondary', mb: 1 }}
            />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('roads_importer_map_grade_blocks_empty')}
            </Typography>
          </Box>
        ) : (
          <>
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', mb: 1.5 }}
            >
              {t('roads_importer_map_grade_blocks_body', {
                count: grades.length,
              })}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {grades.map((grade) => {
                const color = gradeCategoryColorHex(
                  grade.gradeCategory,
                  fallbackColor
                );
                return (
                  <FormControlLabel
                    key={grade.id}
                    sx={{
                      m: 0,
                      py: 0.5,
                      px: 1,
                      borderRadius: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                    control={
                      <Checkbox
                        size="small"
                        checked={selectedIds.has(grade.id)}
                        onChange={() => {
                          toggle(grade.id);
                        }}
                      />
                    }
                    label={
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          flex: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {grade.name}
                        </Typography>
                        {grade.gradeCategory && (
                          <Chip
                            size="small"
                            label={grade.gradeCategory}
                            sx={{
                              bgcolor: alpha(color, 0.15),
                              color,
                              fontWeight: 600,
                            }}
                          />
                        )}
                      </Box>
                    }
                  />
                );
              })}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button variant="text" onClick={onClose} sx={{ fontWeight: 600 }}>
          {t('roads_importer_map_grade_blocks_cancel')}
        </Button>
        {grades.length > 0 && (
          <Button
            variant="contained"
            disabled={selectedIds.size === 0 || mapping}
            startIcon={
              mapping ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <LayersOutlinedIcon />
              )
            }
            onClick={() => {
              void handleConfirm();
            }}
            sx={{ borderRadius: '8px', fontWeight: 600 }}
          >
            {t('roads_importer_map_grade_blocks_confirm', {
              count: selectedIds.size,
            })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
